import type { ValueReferenceEdge } from "@skilitics/nautilus-core-designer-data";

/** Each node against the nodes it reads, indexed once rather than per question. */
export type ValueReferenceGraph = ReadonlyMap<number, number[]>;

export type BuildValueReferenceGraph = () => ValueReferenceGraph;

export const valueReferenceGraphOf = (edges: readonly ValueReferenceEdge[]): ValueReferenceGraph => {
  const successors = new Map<number, number[]>();
  for (const edge of edges) {
    const list = successors.get(edge.fromNodeId);
    if (list) {
      list.push(edge.toNodeId);
    } else {
      successors.set(edge.fromNodeId, [edge.toNodeId]);
    }
  }
  return successors;
};

/**
 * Pre-commit check for binding pickers: would referencing `toNodeId`'s value from `fromNodeId`
 * close a reference loop? True when the target already depends on the source, or is the source.
 *
 * Collecting the design's edges is a whole-design walk that runs each manifest's binding build,
 * so it is deferred until an author actually picks something, and the graph it builds then serves
 * every later question about the same design.
 */
export const wouldCreateValueReferenceCycleCreate = (buildGraph: BuildValueReferenceGraph) => {
  let graph: ValueReferenceGraph | undefined;
  return (fromNodeId: number, toNodeId: number): boolean => {
    if (fromNodeId === toNodeId) return true;
    graph ??= buildGraph();
    return reaches(graph, toNodeId, fromNodeId);
  };
};

export type WouldCreateValueReferenceCycle = ReturnType<typeof wouldCreateValueReferenceCycleCreate>;

const reaches = (successors: ValueReferenceGraph, startNodeId: number, targetNodeId: number): boolean => {
  const seen = new Set([startNodeId]);
  const queue = [startNodeId];
  for (let head = 0; head < queue.length; head++) {
    for (const next of successors.get(queue[head]) ?? []) {
      if (next === targetNodeId) return true;
      if (!seen.has(next)) {
        seen.add(next);
        queue.push(next);
      }
    }
  }
  return false;
};
